/**
 * Mulligan API Client
 *
 * Handles all mulligan-related API operations:
 * - Window management (host-only CRUD)
 * - Swap execution (player self-service)
 * - Status queries (player view)
 * - Audit log retrieval (host-only)
 *
 * Requirements: 1.1, 1.13, 8.1, 10.1
 * Design: Frontend Components → TanStack Query Hooks
 */
import type { ApiResponse } from './types';
import type {
  MulliganWindow,
  CreateMulliganWindowInput,
  UpdateMulliganWindowInput,
  MulliganSwapRequest,
  MulliganSwapResponse,
  MulliganStatusResponse,
  MulliganAuditPage,
  ListMulliganWindowsResponse,
} from './mulligan.types';
import { apiClient } from './client';

// ============================================================================
// Window Management (Host/Cohost only)
// ============================================================================

/**
 * List all Mulligan Windows for a pool, each annotated with isOpen.
 * GET /pools/{poolId}/mulligan-windows
 *
 * @param poolId Pool identifier
 * @returns Array of windows ordered by startTs ascending
 */
export async function listMulliganWindows(
  poolId: string
): Promise<ApiResponse<ListMulliganWindowsResponse>> {
  return apiClient.get<ListMulliganWindowsResponse>(
    `/pools/${poolId}/mulligan-windows`
  );
}

/**
 * Create a new Mulligan Window for a pool.
 * POST /pools/{poolId}/mulligan-windows
 *
 * @param poolId Pool identifier
 * @param input Window configuration (startTs, endTs, maxSwaps)
 * @returns Created MulliganWindow (201)
 */
export async function createMulliganWindow(
  poolId: string,
  input: CreateMulliganWindowInput
): Promise<ApiResponse<MulliganWindow>> {
  return apiClient.post<MulliganWindow>(
    `/pools/${poolId}/mulligan-windows`,
    input
  );
}

/**
 * Update an existing Mulligan Window.
 * PATCH /pools/{poolId}/mulligan-windows/{windowId}
 *
 * @param poolId Pool identifier
 * @param windowId Window identifier
 * @param patch Partial update (startTs, endTs, maxSwaps — at least one required)
 * @returns Updated MulliganWindow (200)
 */
export async function updateMulliganWindow(
  poolId: string,
  windowId: string,
  patch: UpdateMulliganWindowInput
): Promise<ApiResponse<MulliganWindow>> {
  return apiClient.patch<MulliganWindow>(
    `/pools/${poolId}/mulligan-windows/${windowId}`,
    patch
  );
}

/**
 * Delete a Mulligan Window (only if zero swaps have been applied).
 * DELETE /pools/{poolId}/mulligan-windows/{windowId}
 *
 * @param poolId Pool identifier
 * @param windowId Window identifier
 * @returns void (204 No Content)
 */
export async function deleteMulliganWindow(
  poolId: string,
  windowId: string
): Promise<ApiResponse<void>> {
  return apiClient.delete<void>(
    `/pools/${poolId}/mulligan-windows/${windowId}`
  );
}

// ============================================================================
// Swap Operations (Authenticated member, self only)
// ============================================================================

/**
 * Apply a mulligan swap — remove one pick and replace with another celebrity.
 * POST /pools/{poolId}/mulligan-swaps
 *
 * Accepts an optional idempotency key to prevent duplicate swaps on retries.
 * The key is forwarded as the `Idempotency-Key` HTTP header.
 *
 * @param poolId Pool identifier
 * @param input Swap request (outgoing + replacement celebrity)
 * @param idempotencyKey Optional UUID v4 for idempotent submission
 * @returns MulliganSwapResponse on success (200)
 */
export async function applyMulliganSwap(
  poolId: string,
  input: MulliganSwapRequest,
  idempotencyKey?: string
): Promise<ApiResponse<MulliganSwapResponse>> {
  const headers: Record<string, string> | undefined = idempotencyKey
    ? { 'Idempotency-Key': idempotencyKey }
    : undefined;

  return apiClient.post<MulliganSwapResponse>(
    `/pools/${poolId}/mulligan-swaps`,
    input,
    headers
  );
}

// ============================================================================
// Status Query (Authenticated member)
// ============================================================================

/**
 * Get the player's mulligan status for a pool — windows, allowances, locked picks.
 * GET /pools/{poolId}/mulligan-status
 *
 * @param poolId Pool identifier
 * @returns MulliganStatusResponse with windows, lockedPicks, evaluatedAgainstWindowId
 */
export async function getMulliganStatus(
  poolId: string
): Promise<ApiResponse<MulliganStatusResponse>> {
  return apiClient.get<MulliganStatusResponse>(
    `/pools/${poolId}/mulligan-status`
  );
}

// ============================================================================
// Audit Log (Host/Cohost only)
// ============================================================================

/**
 * Get paginated mulligan audit events for a pool.
 * GET /pools/{poolId}/mulligan-audit
 *
 * Uses cursor-based pagination. Pass the `nextCursor` from a previous
 * response to fetch the next page.
 *
 * @param poolId Pool identifier
 * @param cursor Opaque cursor from a previous response (omit for first page)
 * @param limit Number of events per page (1..100, defaults to 100)
 * @returns MulliganAuditPage with events and nextCursor
 */
export async function getMulliganAudit(
  poolId: string,
  cursor?: string,
  limit?: number
): Promise<ApiResponse<MulliganAuditPage>> {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (limit !== undefined) {
    params.set('limit', String(limit));
  }

  const queryString = params.toString();
  const endpoint = `/pools/${poolId}/mulligan-audit${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<MulliganAuditPage>(endpoint);
}
